import { FC, ReactNode } from 'react'
import {
  Text,
  Button,
  Card,
  GreyGrey10,
  GreyGrey100,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  IconArrowRightLeft,
  IconSequence,
  IndigoIndigo60,
  Link,
  InfoBox,
  InfoRow,
  Primary13Regular,
  Primary14ExtendedRegular,
  Primary14SemiBold,
  Primary18Regular,
  Primary18SemiBold,
  Flex
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import styled, { keyframes } from 'styled-components'
import Spinner from 'components/Loading/Spinner'
import { ConsentStatus } from '../../hooks/useOAuthConsent'
import { GenericAppLogo } from './GenericAppLogo'
import { matchKnownOAuthClient } from './knownOAuthClients'

type ConsentScreenProps = {
  status: ConsentStatus
  redirectUri: string | null
  resolvedRedirectUri: string | null
  // Signed-in account context, so the member sees who they're authorizing as.
  accountEmail?: string
  accountWorkspace?: string
  onApprove: () => void
  onDeny: () => void
}

export const ConsentScreen: FC<ConsentScreenProps> = ({
  status,
  redirectUri,
  resolvedRedirectUri,
  accountEmail,
  accountWorkspace,
  onApprove,
  onDeny
}) => (
  <Flex
    data-testid="authorize.page"
    height="100vh"
    width="100%"
    placeItems="center"
    placeContent="center"
    flexDir="column"
    padding="24px"
    gap="24px"
  >
    {match(status)
      .with('loading', () => <Spinner height="32px" width="32px" />)
      .with('error', () => (
        <>
          <ConsentError />
          <SignedInAs
            accountEmail={accountEmail}
            accountWorkspace={accountWorkspace}
          />
        </>
      ))
      .with('success', () => (
        <ConsentSuccess redirectUri={resolvedRedirectUri} />
      ))
      .with('consent', 'submitting', () => (
        <>
          <ConsentCard
            redirectUri={redirectUri}
            accountWorkspace={accountWorkspace}
            isSubmitting={status === 'submitting'}
            onApprove={onApprove}
            onDeny={onDeny}
          />
          <SignedInAs
            accountEmail={accountEmail}
            accountWorkspace={accountWorkspace}
          />
        </>
      ))
      .exhaustive()}
  </Flex>
)

const SignedInAs: FC<{
  accountEmail?: string
  accountWorkspace?: string
}> = ({ accountEmail, accountWorkspace }) =>
  accountEmail ? (
    <Text {...Primary13Regular} color={GreyGrey60} textAlign="center">
      Signed in as {accountEmail}
      {accountWorkspace ? ` · ${accountWorkspace}` : ''}
    </Text>
  ) : null

const ConsentCard: FC<{
  redirectUri: string | null
  accountWorkspace?: string
  isSubmitting: boolean
  onApprove: () => void
  onDeny: () => void
}> = ({ redirectUri, accountWorkspace, isSubmitting, onApprove, onDeny }) => {
  const knownClient = matchKnownOAuthClient(redirectUri)

  // The `client_name` Stytch echoes back is attacker-controlled, so unrecognised
  // clients get a generic label rather than a name they could spoof.
  const appName = knownClient?.name ?? 'This application'
  const website = deriveWebsite(redirectUri)
  const workspaceAccessLabel = accountWorkspace
    ? `your ${accountWorkspace} workspace`
    : 'your Sequence workspace'

  return (
    <Card variant="v2" maxWidth="480px" width="100%" padding="32px">
      <Flex flexDirection="column" gap="24px">
        <Flex flexDirection="column" alignItems="center" gap="16px">
          <Flex alignItems="center" gap="12px">
            <AppBadge logo={knownClient?.logo} />
            <IconArrowRightLeft color={GreyGrey60} size={18} />
            <AppBadge sequence />
          </Flex>

          <Text {...Primary18Regular} color={GreyGrey100} textAlign="center">
            <Text as="span" {...Primary18SemiBold}>
              {appName}
            </Text>{' '}
            is requesting access
          </Text>
        </Flex>

        <DetailsSection website={website} redirectUri={redirectUri} />

        <Text {...Primary13Regular} color={GreyGrey70}>
          By approving, you'll allow {appName} to access {workspaceAccessLabel}.
        </Text>

        <Flex gap="12px">
          <Button
            variant="secondary"
            fullWidth
            onClick={onDeny}
            disabled={isSubmitting}
            style={{ fontSize: '14px', borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onApprove}
            fullWidth
            submitting={isSubmitting}
            submittingText="Authorizing..."
            style={{ fontSize: '14px', borderRadius: '8px' }}
          >
            Approve
          </Button>
        </Flex>
      </Flex>
    </Card>
  )
}

const DetailsSection: FC<{
  website: string | null
  redirectUri: string | null
}> = ({ website, redirectUri }) => {
  if (!website && !redirectUri) {
    return null
  }

  return (
    <Flex flexDirection="column" gap="8px">
      <Text {...Primary14SemiBold} color={GreyGrey80}>
        Details
      </Text>
      <InfoBox paddingTop="16px" paddingBottom="8px" gap="8px">
        {website ? (
          <InfoRow label="Website">
            <Link
              {...Primary13Regular}
              to={website}
              isExternal
              color={IndigoIndigo60}
            >
              {website}
            </Link>
          </InfoRow>
        ) : null}

        {redirectUri ? (
          <InfoRow label="Redirect URIs">
            <Text
              {...Primary13Regular}
              color={GreyGrey80}
              textAlign="right"
              wordBreak="break-all"
              maxW="34ch"
            >
              {redirectUri}
            </Text>
          </InfoRow>
        ) : null}
      </InfoBox>
    </Flex>
  )
}

const fadeIn = keyframes`
	to { opacity: 1; }
`

const DelayedRevealLink = styled(Link)`
  opacity: 0;
  animation: ${fadeIn} 300ms ease 6s forwards;
`

const ConsentSuccess: FC<{
  redirectUri: string | null
}> = ({ redirectUri }) => {
  const appName = matchKnownOAuthClient(redirectUri)?.name ?? 'This application'

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap="24px"
      textAlign="center"
    >
      <Text {...Primary18SemiBold} color={GreyGrey100}>
        You're all set up. Taking you back now…
      </Text>

      {redirectUri ? (
        <DelayedRevealLink
          {...Primary14SemiBold}
          to={redirectUri}
          color={IndigoIndigo60}
        >
          Continue to {appName}
        </DelayedRevealLink>
      ) : null}
    </Flex>
  )
}

const ConsentError: FC = () => (
  <Flex
    flexDirection="column"
    alignItems="center"
    gap="12px"
    textAlign="center"
    maxWidth="480px"
  >
    <Text {...Primary18SemiBold} color={GreyGrey100}>
      Unable to authorize
    </Text>
    <Text {...Primary14ExtendedRegular} color={GreyGrey70}>
      We couldn't process this authorization request. The link may be invalid or
      expired — please try again from the application that sent you here.
    </Text>
  </Flex>
)

const AppBadge: FC<{
  logo?: ReactNode
  sequence?: boolean
}> = ({ logo, sequence = false }) => (
  <Flex
    boxSize="48px"
    borderRadius="12px"
    border={`1px solid ${GreyGrey30}`}
    backgroundColor={GreyGrey10}
    alignItems="center"
    justifyContent="center"
    overflow="hidden"
    flexShrink={0}
  >
    {sequence ? <IconSequence width="28px" /> : (logo ?? <GenericAppLogo />)}
  </Flex>
)

// The redirect URI's origin stands in for the app's website. Returns null for
// relative/invalid redirects (e.g. local previews) so the row is hidden.
const deriveWebsite = (redirectUri: string | null): string | null => {
  if (!redirectUri) {
    return null
  }

  try {
    return new URL(redirectUri).origin
  } catch {
    return null
  }
}
